import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
} from '@mui/material';
import {
  formatPercent,
  getAccuracyColor,
  getSignalColor,
  TYPOGRAPHY,
} from '../../theme/forecastStyles';
import type { StockHistoryWinLossResponse } from '../../lib/api/stock-history';

interface WinLossTableProps {
  winLoss: StockHistoryWinLossResponse;
}

export default function WinLossTable({ winLoss }: WinLossTableProps) {
  return (
    <Card sx={{ bgcolor: 'background.default', height: '100%' }}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 2, mb: 3 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
              Signal Accuracy by Action
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Win rates for BUY / HOLD / SELL forecasts. Bars show hit ratios; table below is audit-ready.
            </Typography>
          </Box>
        </Box>

        {winLoss.length > 0 ? (
          <>
            {/* Micro bar chart */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {winLoss.map((item) => (
                <Box key={item.signal} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      width: 56,
                      fontSize: TYPOGRAPHY.tiny,
                      fontWeight: 500,
                      color: getSignalColor(item.signal),
                    }}
                  >
                    {item.signal}
                  </Typography>
                  <Box sx={{ flex: 1, bgcolor: 'grey.800', borderRadius: 100, height: 6, overflow: 'hidden' }}>
                    <LinearProgress
                      variant="determinate"
                      value={Math.max(0, Math.min(100, item.win_rate))}
                      sx={{
                        height: '100%',
                        bgcolor: 'transparent',
                        '& .MuiLinearProgress-bar': { bgcolor: 'primary.main' }
                      }}
                    />
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{
                      width: 40,
                      textAlign: 'right',
                      fontSize: TYPOGRAPHY.tiny,
                      color: getAccuracyColor(item.win_rate),
                    }}
                  >
                    {formatPercent(item.win_rate)}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* Numeric matrix */}
            <Box sx={{ overflowX: 'auto' }}>
              <TableContainer sx={{ mt: 2 }}>
                <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: 'text.secondary', fontSize: TYPOGRAPHY.tiny }}>Signal</TableCell>
                    <TableCell align="right" sx={{ color: 'text.secondary', fontSize: TYPOGRAPHY.tiny }}>Wins</TableCell>
                    <TableCell align="right" sx={{ color: 'text.secondary', fontSize: TYPOGRAPHY.tiny }}>Losses</TableCell>
                    <TableCell align="right" sx={{ color: 'text.secondary', fontSize: TYPOGRAPHY.tiny }}>Win Rate</TableCell>
                    <TableCell align="right" sx={{ color: 'text.secondary', fontSize: TYPOGRAPHY.tiny }}>Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {winLoss.map((item) => (
                    <TableRow key={item.signal}>
                      <TableCell sx={{ color: getSignalColor(item.signal), fontSize: TYPOGRAPHY.tiny, fontWeight: 500 }}>
                        {item.signal}
                      </TableCell>
                      <TableCell align="right" sx={{ color: 'success.main', fontSize: TYPOGRAPHY.tiny }}>{item.wins}</TableCell>
                      <TableCell align="right" sx={{ color: 'error.main', fontSize: TYPOGRAPHY.tiny }}>{item.losses}</TableCell>
                      <TableCell align="right" sx={{ color: getAccuracyColor(item.win_rate), fontSize: TYPOGRAPHY.tiny }}>
                        {formatPercent(item.win_rate)}
                      </TableCell>
                      <TableCell align="right" sx={{ color: 'text.secondary', fontSize: TYPOGRAPHY.tiny }}>{item.total}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            </Box>
          </>
        ) : (
          <Typography variant="caption" color="text.secondary" sx={{ pt: 2, display: 'block' }}>
            No accuracy data yet. This view populates automatically once forecasts have recorded outcomes.
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}
